import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Sparkles, Bot, Shield, ArrowRight, CheckCircle2, Clock,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, TrendingUp,
    CheckCircle, AlertCircle, ArrowUp, ArrowDown, Activity,
    Calendar, Layers, FileCheck, ExternalLink, HelpCircle
} from "lucide-react";

export default function PostureImprove() {
    const navigate = useNavigate();
    const [improveData, setImproveData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [applying, setApplying] = useState(false);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchImproveData();
    }, []);

    const fetchImproveData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/posture/improve`, { headers });
            setImproveData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load improve data:", err);
        }
        setLoading(false);
    };

    const handleApplyFix = async (item) => {
        setApplying(true);
        try {
            const res = await axios.post(`${backendURL}/api/posture/improve/apply`, { id: item.id }, { headers });
            if (res.data.success) {
                setImproveData(prev => ({
                    ...prev,
                    currentScore: res.data.newScore,
                    items: prev.items.filter(i => i.id !== item.id),
                    rankedCount: prev.items.length - 1
                }));
                setSelectedItem(null);
                setToast(res.data.message);
                setTimeout(() => setToast(null), 3500);
            }
        } catch (err) {
            console.error("Failed to apply fix:", err);
        }
        setApplying(false);
    };

    const items = improveData?.items || [];
    const currentScore = improveData?.currentScore ?? 0;
    const targetScore = improveData?.targetScore ?? 0;

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>04</span>
                <span>/</span>
                <span className="text-slate-400">Know Exactly What To Fix</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4 cols) ═══ */}
                <div className="lg:col-span-5 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Ranked by the points they earn back.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            QueryTel ranks every gap by how much it would lift your score and whether you can fix it now or need to review it. Close the top five and <strong>watch the projection become the number</strong>. No guessing about what matters most.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Every fix ranked by score impact.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Fix it now, or send it for review.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>A clear path from where you are to where you want to be.</span>
                        </div>
                    </div>

                    {/* Navigation Link back to Posture Overview */}
                    <div className="pt-4">
                        <Link
                            to="/posture"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/[0.08] text-[12px] font-bold transition-all"
                        >
                            ← Back to Posture Scorecard
                        </Link>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Clean Elevated Card (Matching Screenshot 04) (7 cols) ═══ */}
                <div className="lg:col-span-7 bg-[#111827] border border-white/[0.08] rounded-2xl p-6 shadow-2xl space-y-6 flex flex-col justify-between">
                    
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-4">
                        <span className="text-[12px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                            Improve Posture
                        </span>
                        <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px] font-mono font-bold">
                            QueryTel {items.length} RANKED
                        </span>
                    </div>

                    {/* Ranked Remediation Rows */}
                    <div className="space-y-1 divide-y divide-white/[0.03]">
                        {items.map((item, idx) => (
                            <div
                                key={item.id}
                                onClick={() => setSelectedItem(item)}
                                className="pt-3 pb-3 flex items-center justify-between gap-4 hover:bg-white/[0.02] px-2 rounded-lg transition-colors cursor-pointer group"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="font-mono text-slate-500 text-[13px] w-4 font-bold">
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <span className="text-white text-[13px] font-medium group-hover:text-blue-400 transition-colors block truncate">
                                            {item.title}
                                        </span>
                                        <span className="text-[10px] text-slate-500 font-mono">
                                            {item.category} • {item.impactBreakdown}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 flex-shrink-0">
                                    <span className="text-emerald-400 font-bold text-[13px] font-mono">
                                        {item.points}
                                    </span>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedItem(item);
                                        }}
                                        className={`text-[11.5px] font-bold font-mono transition-colors ${
                                            item.type === "Fix"
                                                ? "text-blue-400 hover:text-blue-300"
                                                : "text-purple-400 hover:text-purple-300"
                                        }`}
                                    >
                                        {item.type} →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bottom Progress Projection Bar */}
                    <div className="pt-4 border-t border-white/[0.06] flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11.5px] font-mono">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 uppercase">Posture Score:</span>
                            <span className="text-orange-400 font-black text-[13px]">{currentScore}</span>
                            <span className="text-slate-600">→</span>
                            <span className="text-purple-400 font-black text-[13px]">{targetScore}</span>
                        </div>

                        <span className="text-purple-400 font-bold">
                            {improveData?.totalGain || "+18 pts"} if you resolve active recommendations
                        </span>
                    </div>

                </div>

            </div>

            {/* ─── Item Detail / Apply Fix Modal ─── */}
            {selectedItem && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-purple-400" />
                                <h3 className="text-[14px] font-bold text-white">
                                    {selectedItem.title}
                                </h3>
                            </div>
                            <button onClick={() => setSelectedItem(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Category & Frameworks</label>
                                <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg text-slate-300 font-mono text-[11px]">
                                    {selectedItem.category} • {selectedItem.impactBreakdown}
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Description & Evidence</label>
                                <p className="text-slate-300 leading-relaxed bg-black/30 p-3 rounded-lg border border-white/[0.04]">
                                    {selectedItem.description}
                                </p>
                            </div>

                            <div className="p-3 bg-purple-950/20 border border-purple-500/30 rounded-xl flex items-center justify-between">
                                <span className="text-purple-300 font-mono font-bold">Earned Points:</span>
                                <span className="text-emerald-400 font-mono font-black text-[15px]">{selectedItem.points} pts</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                            <button
                                type="button"
                                onClick={() => setSelectedItem(null)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg font-mono text-[11.5px]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={applying}
                                onClick={() => handleApplyFix(selectedItem)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md shadow-blue-600/20 font-mono text-[11.5px] disabled:opacity-50"
                            >
                                {applying ? "Applying..." : `Apply Fix (${selectedItem.points} pts)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
